/**
 * Engagement Tracker Provider
 *
 * Tracks user engagement for intelligent feedback prompt timing.
 * Implements criteria for high-signal, low-friction feedback collection.
 */

import { IlocalStorageProvider } from "./localStorageProvider";

const STORAGE_KEYS = {
  ACCOUNT_CREATED_AT: "accountCreatedAt",
  ENGAGEMENT_SESSIONS: "engagementSessions",
  CURRENT_SESSION: "currentEngagementSession",
  PAGE_VIEWS: "engagementPageViews",
  WORKFLOW_COMPLETIONS: "engagementWorkflows",
  MILESTONES: "engagementMilestones",
  POSITIVE_EVENTS: "engagementPositiveEvents",
  CURRENT_MODULE_CONTEXT: "currentModuleContext",
  LAST_SURVEY_PROMPT: "lastSurveyPromptAt",
  LAST_SURVEY_SUBMITTED: "surveyTime", // Reuse existing key
  SURVEY_SNOOZED_UNTIL: "surveySnoozedUntil", // Reuse existing key
} as const;

export const EngagementTrackerProviderFactory = ({
  localStorageProvider,
}: {
  localStorageProvider: IlocalStorageProvider;
}): IEngagementTrackerProvider => {
  // Helper to get account creation date
  const getAccountCreatedAt = (): string | null => {
    return localStorageProvider.get(STORAGE_KEYS.ACCOUNT_CREATED_AT);
  };

  // Helper to set account creation date (one-time)
  const ensureAccountCreatedAt = (): void => {
    if (!getAccountCreatedAt()) {
      localStorageProvider.set(
        STORAGE_KEYS.ACCOUNT_CREATED_AT,
        new Date().toISOString()
      );
    }
  };

  // Session tracking
  const startSession = (): void => {
    ensureAccountCreatedAt();

    const session: IEngagementSession = {
      startTime: new Date().toISOString(),
      isMeaningful: false,
      pageViews: [],
      activities: [],
    };

    localStorageProvider.set(
      STORAGE_KEYS.CURRENT_SESSION,
      JSON.stringify(session)
    );
  };

  const endSession = (): void => {
    const currentSessionStr = localStorageProvider.get(
      STORAGE_KEYS.CURRENT_SESSION
    );
    if (!currentSessionStr) return;

    try {
      const session: IEngagementSession = JSON.parse(currentSessionStr);
      const endTime = new Date();
      const startTime = new Date(session.startTime);
      const duration = endTime.getTime() - startTime.getTime();

      session.endTime = endTime.toISOString();
      session.duration = duration;

      // A session is meaningful if it's >2 minutes OR has activities
      session.isMeaningful =
        duration > 2 * 60 * 1000 || session.activities.length > 0;

      // Save to sessions history
      const sessionsStr =
        localStorageProvider.get(STORAGE_KEYS.ENGAGEMENT_SESSIONS) || "[]";
      const sessions: IEngagementSession[] = JSON.parse(sessionsStr);
      sessions.push(session);

      // Keep only last 100 sessions to prevent storage bloat
      if (sessions.length > 100) {
        sessions.splice(0, sessions.length - 100);
      }

      localStorageProvider.set(
        STORAGE_KEYS.ENGAGEMENT_SESSIONS,
        JSON.stringify(sessions)
      );
      localStorageProvider.remove(STORAGE_KEYS.CURRENT_SESSION);
    } catch (error) {
      console.error("Error ending session:", error);
    }
  };

  const getMeaningfulSessionCount = (): number => {
    try {
      const sessionsStr =
        localStorageProvider.get(STORAGE_KEYS.ENGAGEMENT_SESSIONS) || "[]";
      const sessions: IEngagementSession[] = JSON.parse(sessionsStr);
      return sessions.filter((s) => s.isMeaningful).length;
    } catch (error) {
      console.error("Error getting meaningful session count:", error);
      return 0;
    }
  };

  // Page view tracking
  const trackPageView = (module: string, route: string): void => {
    ensureAccountCreatedAt();

    try {
      // Add to current session
      const currentSessionStr = localStorageProvider.get(
        STORAGE_KEYS.CURRENT_SESSION
      );
      if (currentSessionStr) {
        const session: IEngagementSession = JSON.parse(currentSessionStr);
        session.pageViews.push(`${module}:${route}`);
        localStorageProvider.set(
          STORAGE_KEYS.CURRENT_SESSION,
          JSON.stringify(session)
        );
      }

      // Track globally
      const pageViewsStr =
        localStorageProvider.get(STORAGE_KEYS.PAGE_VIEWS) || "{}";
      const pageViews: Record<string, number> = JSON.parse(pageViewsStr);

      pageViews[module] = (pageViews[module] || 0) + 1;

      localStorageProvider.set(
        STORAGE_KEYS.PAGE_VIEWS,
        JSON.stringify(pageViews)
      );

      // Update current module context
      setCurrentModuleContext({
        moduleName: module,
        route,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error tracking page view:", error);
    }
  };

  const getUniqueModuleCount = (): number => {
    try {
      const pageViewsStr =
        localStorageProvider.get(STORAGE_KEYS.PAGE_VIEWS) || "{}";
      const pageViews: Record<string, number> = JSON.parse(pageViewsStr);
      return Object.keys(pageViews).length;
    } catch (error) {
      console.error("Error getting unique module count:", error);
      return 0;
    }
  };

  const getPageViewsByModule = (): Record<string, number> => {
    try {
      const pageViewsStr =
        localStorageProvider.get(STORAGE_KEYS.PAGE_VIEWS) || "{}";
      return JSON.parse(pageViewsStr);
    } catch (error) {
      console.error("Error getting page views by module:", error);
      return {};
    }
  };

  // Workflow completion tracking
  const trackWorkflowCompletion = (
    workflowType: string,
    context?: Record<string, any>
  ): void => {
    ensureAccountCreatedAt();

    try {
      // Add to current session activities
      const currentSessionStr = localStorageProvider.get(
        STORAGE_KEYS.CURRENT_SESSION
      );
      if (currentSessionStr) {
        const session: IEngagementSession = JSON.parse(currentSessionStr);
        session.activities.push(`workflow:${workflowType}`);
        localStorageProvider.set(
          STORAGE_KEYS.CURRENT_SESSION,
          JSON.stringify(session)
        );
      }

      const completion: IWorkflowCompletion = {
        workflowType,
        completedAt: new Date().toISOString(),
        context,
      };

      const completionsStr =
        localStorageProvider.get(STORAGE_KEYS.WORKFLOW_COMPLETIONS) || "[]";
      const completions: IWorkflowCompletion[] = JSON.parse(completionsStr);
      completions.push(completion);

      // Keep only last 50 completions
      if (completions.length > 50) {
        completions.splice(0, completions.length - 50);
      }

      localStorageProvider.set(
        STORAGE_KEYS.WORKFLOW_COMPLETIONS,
        JSON.stringify(completions)
      );
    } catch (error) {
      console.error("Error tracking workflow completion:", error);
    }
  };

  const getWorkflowCompletions = (): IWorkflowCompletion[] => {
    try {
      const completionsStr =
        localStorageProvider.get(STORAGE_KEYS.WORKFLOW_COMPLETIONS) || "[]";
      return JSON.parse(completionsStr);
    } catch (error) {
      console.error("Error getting workflow completions:", error);
      return [];
    }
  };

  // Milestone tracking
  const trackMilestone = (
    milestoneType: string,
    value: number,
    context?: Record<string, any>
  ): void => {
    ensureAccountCreatedAt();

    try {
      const milestone: IMilestone = {
        milestoneType,
        value,
        achievedAt: new Date().toISOString(),
        context,
      };

      const milestonesStr =
        localStorageProvider.get(STORAGE_KEYS.MILESTONES) || "[]";
      const milestones: IMilestone[] = JSON.parse(milestonesStr);
      milestones.push(milestone);

      // Keep only last 30 milestones
      if (milestones.length > 30) {
        milestones.splice(0, milestones.length - 30);
      }

      localStorageProvider.set(
        STORAGE_KEYS.MILESTONES,
        JSON.stringify(milestones)
      );
    } catch (error) {
      console.error("Error tracking milestone:", error);
    }
  };

  const getMilestones = (): IMilestone[] => {
    try {
      const milestonesStr =
        localStorageProvider.get(STORAGE_KEYS.MILESTONES) || "[]";
      return JSON.parse(milestonesStr);
    } catch (error) {
      console.error("Error getting milestones:", error);
      return [];
    }
  };

  // Positive event tracking
  const trackPositiveEvent = (
    eventType: string,
    context?: Record<string, any>
  ): void => {
    ensureAccountCreatedAt();

    try {
      const event: IPositiveEvent = {
        eventType,
        occurredAt: new Date().toISOString(),
        context,
      };

      const eventsStr =
        localStorageProvider.get(STORAGE_KEYS.POSITIVE_EVENTS) || "[]";
      const events: IPositiveEvent[] = JSON.parse(eventsStr);
      events.push(event);

      // Keep only last 30 events
      if (events.length > 30) {
        events.splice(0, events.length - 30);
      }

      localStorageProvider.set(
        STORAGE_KEYS.POSITIVE_EVENTS,
        JSON.stringify(events)
      );
    } catch (error) {
      console.error("Error tracking positive event:", error);
    }
  };

  const getPositiveEvents = (): IPositiveEvent[] => {
    try {
      const eventsStr =
        localStorageProvider.get(STORAGE_KEYS.POSITIVE_EVENTS) || "[]";
      return JSON.parse(eventsStr);
    } catch (error) {
      console.error("Error getting positive events:", error);
      return [];
    }
  };

  // Eligibility checking
  const isEligibleForSurvey = (): IEligibilityResult => {
    const reasons: string[] = [];
    let eligible = true;

    // Get basic data
    const accountCreatedAt = getAccountCreatedAt();
    const meaningfulSessions = getMeaningfulSessionCount();
    const uniqueModules = getUniqueModuleCount();
    const workflowCompletions = getWorkflowCompletions().length;
    const lastPromptStr = localStorageProvider.get(
      STORAGE_KEYS.LAST_SURVEY_PROMPT
    );
    const lastSubmittedStr = localStorageProvider.get(
      STORAGE_KEYS.LAST_SURVEY_SUBMITTED
    );
    const snoozedUntilStr = localStorageProvider.get(
      STORAGE_KEYS.SURVEY_SNOOZED_UNTIL
    );

    const now = new Date();

    // Calculate days since signup
    let daysSinceSignup = 0;
    if (accountCreatedAt) {
      const signupDate = new Date(accountCreatedAt);
      daysSinceSignup =
        (now.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24);
    } else {
      eligible = false;
      reasons.push("Account creation date not tracked");
    }

    // Calculate days since last prompt
    let daysSinceLastPrompt: number | null = null;
    if (lastPromptStr) {
      const lastPromptDate = new Date(lastPromptStr);
      daysSinceLastPrompt =
        (now.getTime() - lastPromptDate.getTime()) / (1000 * 60 * 60 * 24);
    }

    // Check: Must be at least 7 days since signup
    if (daysSinceSignup < 7) {
      eligible = false;
      reasons.push(
        `Only ${Math.floor(daysSinceSignup)} days since signup (need 7)`
      );
    } else {
      reasons.push(`✓ ${Math.floor(daysSinceSignup)} days since signup`);
    }

    // Check: If snoozed, must wait until snooze expires
    if (snoozedUntilStr) {
      const snoozedUntil = new Date(snoozedUntilStr);
      if (now < snoozedUntil) {
        eligible = false;
        const daysRemaining =
          (snoozedUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        reasons.push(`Snoozed for ${Math.ceil(daysRemaining)} more days`);
      } else {
        reasons.push("✓ Snooze period expired");
      }
    }

    // Check: Must wait at least 30 days between prompts (or 90 if previously submitted)
    if (daysSinceLastPrompt !== null) {
      const requiredDays = lastSubmittedStr ? 90 : 30;
      if (daysSinceLastPrompt < requiredDays) {
        eligible = false;
        reasons.push(
          `Only ${Math.floor(
            daysSinceLastPrompt
          )} days since last prompt (need ${requiredDays})`
        );
      } else {
        reasons.push(
          `✓ ${Math.floor(daysSinceLastPrompt)} days since last prompt`
        );
      }
    }

    // Check engagement depth: Need at least ONE of these conditions
    const hasEnoughSessions = meaningfulSessions >= 3;
    const hasEnoughModules = uniqueModules >= 5;
    const hasWorkflowCompletion = workflowCompletions > 0;

    if (!hasEnoughSessions && !hasEnoughModules && !hasWorkflowCompletion) {
      eligible = false;
      reasons.push(
        `Insufficient engagement: ${meaningfulSessions}/3 sessions, ${uniqueModules}/5 modules, ${workflowCompletions} workflows`
      );
    } else {
      if (hasEnoughSessions)
        reasons.push(`✓ ${meaningfulSessions} meaningful sessions`);
      if (hasEnoughModules)
        reasons.push(`✓ ${uniqueModules} unique modules visited`);
      if (hasWorkflowCompletion)
        reasons.push(`✓ ${workflowCompletions} workflows completed`);
    }

    // Calculate engagement score (0-100)
    const engagementScore = Math.min(
      100,
      meaningfulSessions * 10 + uniqueModules * 5 + workflowCompletions * 15
    );

    // Sampling: Show to all eligible users (100%)
    // The strict eligibility criteria above already ensure we don't over-survey
    const shouldSample = true;

    return {
      eligible,
      reasons,
      engagementScore,
      daysSinceSignup,
      daysSinceLastPrompt,
      meaningfulSessions,
      uniqueModules,
      workflowCompletions,
      shouldSample,
    };
  };

  // Current module context
  const getCurrentModuleContext = (): IModuleContext | null => {
    try {
      const contextStr = localStorageProvider.get(
        STORAGE_KEYS.CURRENT_MODULE_CONTEXT
      );
      if (!contextStr) return null;
      return JSON.parse(contextStr);
    } catch (error) {
      console.error("Error getting current module context:", error);
      return null;
    }
  };

  const setCurrentModuleContext = (context: IModuleContext): void => {
    try {
      localStorageProvider.set(
        STORAGE_KEYS.CURRENT_MODULE_CONTEXT,
        JSON.stringify(context)
      );
    } catch (error) {
      console.error("Error setting current module context:", error);
    }
  };

  // Reset/cleanup
  const resetEngagementData = (): void => {
    Object.values(STORAGE_KEYS).forEach((key) => {
      if (key !== STORAGE_KEYS.ACCOUNT_CREATED_AT) {
        localStorageProvider.remove(key);
      }
    });
  };

  // Get engagement summary
  const getEngagementSummary = (): IEngagementSummary => {
    try {
      const sessionsStr =
        localStorageProvider.get(STORAGE_KEYS.ENGAGEMENT_SESSIONS) || "[]";
      const sessions: IEngagementSession[] = JSON.parse(sessionsStr);

      return {
        accountCreatedAt: getAccountCreatedAt(),
        lastSurveyPromptAt: localStorageProvider.get(
          STORAGE_KEYS.LAST_SURVEY_PROMPT
        ),
        lastSurveySubmittedAt: localStorageProvider.get(
          STORAGE_KEYS.LAST_SURVEY_SUBMITTED
        ),
        snoozedUntil: localStorageProvider.get(
          STORAGE_KEYS.SURVEY_SNOOZED_UNTIL
        ),
        meaningfulSessions: getMeaningfulSessionCount(),
        totalSessions: sessions.length,
        uniqueModulesVisited: getUniqueModuleCount(),
        workflowCompletionsCount: getWorkflowCompletions().length,
        milestonesAchieved: getMilestones().length,
        positiveEventsCount: getPositiveEvents().length,
        currentContext: getCurrentModuleContext(),
        sessions,
      };
    } catch (error) {
      console.error("Error getting engagement summary:", error);
      return {
        accountCreatedAt: null,
        lastSurveyPromptAt: null,
        lastSurveySubmittedAt: null,
        snoozedUntil: null,
        meaningfulSessions: 0,
        totalSessions: 0,
        uniqueModulesVisited: 0,
        workflowCompletionsCount: 0,
        milestonesAchieved: 0,
        positiveEventsCount: 0,
        currentContext: null,
        sessions: [],
      };
    }
  };

  return {
    startSession,
    endSession,
    getMeaningfulSessionCount,
    trackPageView,
    getUniqueModuleCount,
    getPageViewsByModule,
    trackWorkflowCompletion,
    getWorkflowCompletions,
    trackMilestone,
    getMilestones,
    trackPositiveEvent,
    getPositiveEvents,
    isEligibleForSurvey,
    getCurrentModuleContext,
    setCurrentModuleContext,
    resetEngagementData,
    getEngagementSummary,
  };
};
